import React, { useEffect, useState } from "react";
import Select from "react-select";
import { getQuizzes, deleteQuiz, updateQuiz, fetchCategories } from "../services/api";
import { Link } from "react-router-dom";

function QuizEditor() {
  const [quizzes, setQuizzes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editQuiz, setEditQuiz] = useState(null);
  const [newQuizName, setNewQuizName] = useState("");
  const [newQuizImgUrl, setNewQuizImgUrl] = useState("");
  const [newQuizDescription, setNewQuizDescription] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("en_US"); // Language dropdown state
    const [selectedProject, setSelectedProject] = useState("jagranquiz"); 
  const [isActive, setIsActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState(""); // Search query state

  useEffect(() => {
    const userRole = sessionStorage.getItem("userRole");
  const userProject = sessionStorage.getItem("project");

  // Determine if a query filter parameter needs to be appended
  const projectFilter = userRole !== "super_admin" ? userProject : null;

    const fetchQuizzes = async () => {
      try {
        const response = await getQuizzes(projectFilter);
        setQuizzes(response.data.data);
      } catch (err) {
        setError("Failed to fetch quizzes");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    const fetchCategoriesData = async () => {
      try {
        const response = await fetchCategories(projectFilter);
        setCategories(response);
      } catch (err) {
        setError("Failed to fetch categories");
        console.error(err);
      }
    };

    fetchQuizzes();
    fetchCategoriesData();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this quiz?")) {
      try {
        await deleteQuiz(id);
        setQuizzes(quizzes.filter((quiz) => quiz.quiz_id !== id));
      } catch (err) {
        setError("Failed to delete quiz");
        console.error(err);
      }
    }
  };

  const handleEdit = (quiz) => {
    setEditQuiz(quiz);
    setNewQuizName(quiz.quiz_name);
    setNewQuizDescription(quiz.quiz_description);
    setNewQuizImgUrl(quiz.quiz_img_url);
    setSelectedCategoryId(quiz.category?.cat_id || "");
    setSelectedLanguage(quiz.language || "en_US"); // Set selected language
    setSelectedProject(quiz.project || "jagranquiz");
    setIsActive(quiz.active);
  };

  const handleUpdate = async () => {
    if (!newQuizName || !newQuizDescription || !selectedCategoryId || !selectedLanguage || !selectedProject) {
      return alert("All fields are required, including the category and language.");
    }

    const selectedCategory = categories.find(
      (category) => category.cat_id === selectedCategoryId
    );

    if (!selectedCategory) {
      return alert("Invalid category selected.");
    }

    try {
      const payload = {
        quiz_id: editQuiz.quiz_id,
        quiz_name: newQuizName,
        quiz_description: newQuizDescription,
        quiz_img_url: newQuizImgUrl,
        active: isActive,
        language: selectedLanguage,
        project : selectedProject, // Include language in payload
        category: {
          cat_id: selectedCategory.cat_id,
          cat_name: selectedCategory.cat_name,
        },
      };

      await updateQuiz(payload);

      setQuizzes(
        quizzes.map((quiz) =>
          quiz.quiz_id === editQuiz.quiz_id ? { ...quiz, ...payload } : quiz
        )
      );

      setEditQuiz(null);
    } catch (err) {
      setError("Failed to update quiz");
      console.error(err);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const filteredQuizzes = quizzes.filter((quiz) => {
    const quizNameMatch = quiz.quiz_name.toLowerCase().includes(searchQuery);
    const categoryNameMatch = quiz.category?.cat_name?.toLowerCase().includes(searchQuery);
    return quizNameMatch || categoryNameMatch;
  });

  const languageMapping = {
    en_US: "English",
    hi_IN: "Hindi",
  };

  const projectMapping = {
    jagranquiz: "Jagran",
    joshquiz: "Josh",
  };

  if (loading) return <p>Loading quizzes...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Quiz Manager</h1>
      <div style={styles.buttonGroup}>
        <Link to="/create-quiz" style={styles.createButton}>
          + Create New Quiz
        </Link>
        <Link to="/add-category" style={styles.addCategoryButton}>
          + Add Category
        </Link>
      </div>
      <div style={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search by quiz name or category"
          value={searchQuery}
          onChange={handleSearch}
          style={styles.searchInput}
        />
      </div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.tableHeader}>Quiz ID</th>
            <th style={styles.tableHeader}>Quiz Name</th>
            <th style={styles.tableHeader}>Description</th>
            <th style={styles.tableHeader}>Category</th>
            <th style={styles.tableHeader}>Language</th>
             <th style={styles.tableHeader}>Project</th>
            <th style={styles.tableHeader}>Active</th>
            <th style={styles.tableHeader}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredQuizzes.map((quiz) => (
            <tr key={quiz.quiz_id} style={styles.tableRow}>
              <td>{quiz.quiz_id}</td>
              <td>{quiz.quiz_name}</td>
              <td>{quiz.quiz_description}</td>
              <td>{quiz.category?.cat_name}</td>
              <td>{languageMapping[quiz.language] || quiz.language}</td>
               <td>{projectMapping[quiz.project] || quiz.project}</td>
              <td>{quiz.active ? "Yes" : "No"}</td>
              <td>
                <Link to={`/quizzes/${quiz.quiz_id}`} style={styles.viewButton}>
                  View
                </Link>
                <button onClick={() => handleEdit(quiz)} style={styles.editButton}>
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editQuiz && (
        <div style={styles.modal}>
          <h2 style={styles.modalTitle}>Edit Quiz</h2>
          <label style={styles.label}>Quiz Name:</label>
          <input
            type="text"
            value={newQuizName}
            onChange={(e) => setNewQuizName(e.target.value)}
            style={styles.input}
          />
          <label style={styles.label}>Quiz Description:</label>
          <textarea
            value={newQuizDescription}
            onChange={(e) => setNewQuizDescription(e.target.value)}
            style={styles.textarea}
          />
          <label style={styles.label}>Quiz Image URL:</label>
          <input
            value={newQuizImgUrl}
            onChange={(e) => setNewQuizImgUrl(e.target.value)}
            style={styles.input}
          />
          <label style={styles.label}>Category:</label>
          <Select
            styles={{
              control: (provided) => ({
                ...provided,
                width: "400px",
                margin: "0 auto",
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "5px",
              }),
              menu: (provided) => ({
                ...provided,
                width: "400px",
                margin: "0 auto",
              }),
            }}
            value={
              categories
                .map((category) => ({
                  value: category.cat_id,
                  label: `${category.cat_name}${
                    category.parent ? ` (${category.parent.cat_name})` : ""
                  }`,
                }))
                .find((option) => option.value === selectedCategoryId) || null
            }
            onChange={(selectedOption) => setSelectedCategoryId(selectedOption?.value || "")}
            options={categories.map((category) => ({
              value: category.cat_id,
              label: `${category.cat_name}${
                category.parent ? ` (${category.parent.cat_name})` : ""
              }`,
            }))}
            placeholder="Select a category"
            isClearable
            isSearchable
          />
          <label style={styles.label}>Language:</label>
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            style={styles.select}
          >
            <option value="en_US">English</option>
            <option value="hi_IN">Hindi</option>
          </select>


          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            style={styles.select}
          >
            <option value="jagranquiz">Jagran</option>
            <option value="joshquiz">Josh</option>
          </select>

          <label style={styles.label}>Active Status:</label>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              style={styles.checkbox}
            />
            <span>{isActive ? "Active" : "Inactive"}</span>
          </div>
          <div style={styles.buttonContainer}>
            <button onClick={handleUpdate} style={styles.updateButton}>
              Update
            </button>
            <button onClick={() => setEditQuiz(null)} style={styles.cancelButton}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


const styles = {
  container: { 
    padding: "30px", 
    maxWidth: "1000px", 
    margin: "0 auto" 
  },
  title: { 
    marginBottom: "20px", 
    fontSize: "28px", 
    color: "#333", 
    fontWeight: "600", 
    textAlign: "center" 
  },
  buttonGroup: { 
    display: "flex", 
    justifyContent: "space-between", 
    marginBottom: "20px" 
  },
  createButton: {
    display: "inline-block",
    padding: "10px 20px",
    backgroundColor: "#4CAF50",
    color: "#fff",
    textDecoration: "none",
    borderRadius: "5px",
    fontWeight: "bold",
    textAlign: "center",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
  },
  addCategoryButton: {
    display: "inline-block",
    padding: "10px 20px",
    backgroundColor: "#007bff",
    color: "#fff",
    textDecoration: "none",
    borderRadius: "5px",
    fontWeight: "bold",
    textAlign: "center",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
  },
  table: { 
    width: "100%", 
    borderCollapse: "collapse", 
    marginBottom: "30px", 
    backgroundColor: "#f8f9fa" 
  },
  tableHeader: { 
    padding: "12px 15px", 
    backgroundColor: "#007bff", 
    color: "#fff", 
    fontWeight: "bold", 
    textAlign: "left" 
  },
  tableRow: {
    borderBottom: "1px solid #ddd",
    "&:hover": { backgroundColor: "#f1f1f1" }
  },
  viewButton: {
    color: "#007bff",
    marginRight: "10px",
    textDecoration: "none",
    fontWeight: "500",
    cursor: "pointer",
  },
  editButton: { 
    backgroundColor: "#ffc107", 
    color: "#fff", 
    border: "none", 
    padding: "8px 12px", 
    borderRadius: "4px", 
    cursor: "pointer", 
    marginRight: "10px",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)"
  },
  deleteButton: { 
    backgroundColor: "#dc3545", 
    color: "#fff", 
    border: "none", 
    padding: "8px 12px", 
    borderRadius: "4px", 
    cursor: "pointer",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)"
  },
  
  // Modal Styling
  modal: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#fff",
    padding: "30px 40px",
    borderRadius: "8px",
    boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.3)",
    zIndex: "1000",
    width: "400px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    maxHeight: "80vh", // Set a max height for the modal
    overflowY: "auto", // Allow vertical scrolling when content overflows
  },
  modalTitle: { 
    fontSize: "24px", 
    marginBottom: "15px", 
    color: "#333", 
    fontWeight: "600" 
  },
  label: { 
    alignSelf: "flex-start", 
    marginTop: "10px", 
    marginBottom: "5px", 
    color: "#333", 
    fontWeight: "500" 
  },
  input: { 
    width: "100%", 
    padding: "10px", 
    border: "1px solid #ccc", 
    borderRadius: "4px", 
    marginBottom: "15px" 
  },
  textarea: { 
    width: "100%", 
    padding: "10px", 
    border: "1px solid #ccc", 
    borderRadius: "4px", 
    minHeight: "80px", 
    marginBottom: "15px" 
  },
  buttonContainer: { 
    display: "flex", 
    justifyContent: "space-between", 
    width: "100%", 
    marginTop: "15px" 
  },
  updateButton: {
    padding: "10px 20px",
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: "bold",
    flex: "1",
    marginRight: "10px",
    transition: "background-color 0.3s ease",
  },
  cancelButton: {
    padding: "10px 20px",
    backgroundColor: "#6c757d",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: "bold",
    flex: "1",
    transition: "background-color 0.3s ease",
  },
  checkbox: {
    width: "20px",
    height: "20px",
  },
  select : {
    width: "100%",
    padding: "10px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    marginBottom: "15px",
    fontSize: "16px",
    backgroundColor: "#f8f9fa",
  },
  searchContainer: {
    marginBottom: "20px",
    textAlign: "center",
  },
  searchInput: {
    width: "50%",
    padding: "10px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "16px",
  },
};

export default QuizEditor;
